import React, { useEffect, useMemo, useState, useRef } from "react";
import moment from "moment";
import PageTableHeader from "../../../components/pageTableHeader/pageTableHeader";
import CommonTable from "../../../components/table/table";
import { staffAttendanceReportByDate } from "../../../../services/workspace/report.service";
import { useCommonFetch } from "../../../../hooks/useCommonFetch";
import YearMonthFilter from "../../../components/filter/yearMonthFilter";

const items = [{ label: "My Attendance Reports", path: "" }];

const MyAttendanceReports = () => {
  const [selectedDate, setSelectedDate] = useState(() =>
    moment().startOf("month").format("YYYY-MM-DD")
  );

  // Handle month selection from YearMonthFilter
  const handleMonthChange = (selectedDateObj) => {
    if (selectedDateObj) {
      const date = moment(selectedDateObj).startOf("month").format("YYYY-MM-DD");
      setSelectedDate(date);
    }
  };

  const {
    loading,
    data: tableData,
    total: totalData,
    currentPage,
    search,
    selectedId,
    action,
    handleSearchChange,
    handlePageChange,
    openModal,
    closeModal,
    handleCreate,
    handleUpdate,
    handleDelete,
    sortingUIProps,
    refreshData,
  } = useCommonFetch(staffAttendanceReportByDate, {
    dataPath: "attendance.data",
    totalPath: "attendance.meta.total",
    errorMessage: "Failed to fetch attendance report!",
    debounceDelay: 500,
    autoFetch: false, // Manual control only
    dataTransformer: (data) => {
      if (Array.isArray(data)) {
        const transformedData = data.map((item, index) => {
          // Handle nested attendance object structure
          const attendance = item.attendance || {};
          const firstRecord = Array.isArray(attendance.records) && attendance.records.length > 0 
            ? attendance.records[0] 
            : {};

          const start_time = firstRecord.start_time || attendance.start_time || "-";
          const end_time = firstRecord.end_time || attendance.end_time || "-";
          const total_hours = firstRecord.total_hours || attendance.total_hours || "-";

          // Determine status
          let statusText = "-";
          if (item.is_holiday) {
            statusText = "Holiday";
          } else if (item.is_leave) {
            statusText = "Leave";
          } else if (item.is_weekend) {
            statusText = "Weekend";
          } else if (start_time && start_time !== "-" && end_time && end_time !== "-") {
            statusText = "Present";
          } else if (start_time && start_time !== "-") {
            statusText = "Partial";
          } else {
            statusText = "Absent";
          }

          return {
            ...item,
            id: {
              data: item.id || item.uuid || `${item.date}-${index}`,
              className: "",
              sticky: false,
              variant: "text",
              customCases: null,
            },
            date: {
              data: item.date || item.attendance_date || 
                (item.created_at ? moment(item.created_at).format("YYYY-MM-DD") : "-"),
              className: "",
              sticky: false,
              variant: "date",
              customCases: null,
            },
            status: {
              data: statusText,
              className: "",
              sticky: false,
              variant: "text",
              customCases: null,
            },
            check_in: {
              data: start_time,
              className: "",
              variant: "text",
              customCases: null,
            },
            check_out: {
              data: end_time,
              className: "",
              variant: "text",
              customCases: null,
            },
            working_hours: {
              data: total_hours,
              className: "",
              sticky: false,
              variant: "text",
              customCases: null,
            },
            late: {
              data: item.late || item.is_late || item.late_minutes || item.late_count || "-",
              className: "",
              sticky: false,
              variant: "text",
              customCases: null,
            },
            actions: {
              data: item.id || item.uuid || `${item.date}-${index}`,
              className: "",
              sticky: false,
              variant: "",
              customCases: null,
            },
          };
        });

        return {
          data: transformedData,
          action: {},
        };
      }
      return { data: [], action: {} };
    },
  });

  // Initial fetch on mount (only once)
  useEffect(() => {
    refreshData(
      {
        date: selectedDate,
      },
      true
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only on mount

  // Refresh data when date changes (debounced)
  const prevDate = useRef(selectedDate);
  useEffect(() => {
    // Skip if date hasn't actually changed
    if (prevDate.current === selectedDate) {
      return;
    }
    
    prevDate.current = selectedDate;
    
    const timeoutId = setTimeout(() => {
      refreshData(
        {
          date: selectedDate,
        },
        true
      );
    }, 500); // Debounce 500ms

    return () => clearTimeout(timeoutId);
  }, [selectedDate, refreshData]);

  const TableHeaderData = [
    { label: "Date", key: "date" },
    { label: "Status", key: "status" },
    { label: "Check In", key: "check_in" },
    { label: "Check Out", key: "check_out" },
    { label: "Working (hrs)", key: "working_hours" },
    { label: "Late", key: "late" },
  ];

  return (
    <div className="flex flex-col gap-4">
      <PageTableHeader breadcrumbs={items} />

      <div className="flex items-end justify-end gap-4 flex-wrap">
        <div className="flex items-center gap-4">
          <div className="w-60">
            <YearMonthFilter handleChangeDate={handleMonthChange} />
          </div>
        </div>
      </div>

      <div className="bg-white">
        <CommonTable
          TableHeaderData={TableHeaderData}
          tabledata={tableData?.data || []}
          loading={loading}
          showActions={false}
          noDataText="No attendance data found for selected dates."
        />
      </div>
    </div>
  );
};

export default MyAttendanceReports;

